import { App } from 'BMapsSrc/BMapsApp';
import { UserActions } from 'BMapsCmds';
import { ensureArray } from 'BMapsUtil/js_utils';
import { Compound } from 'BMapsModel';
import { createJob, loadResult } from 'BMapsRedux/jobs/access';
import { MdSimulationJob } from 'BMapsSrc/jobman/jobs/MdSimulationJob';
import { targetOptions, targetProblem } from 'BMapsSrc/integrations/compute_targets';

/**
 * The MD engines BMaps can submit to, one jobtype each (the wire contract with
 * the backend's md_workflow.get_details). Literal jobtypes for the same reason
 * as FEP_PROGRAMS: the job registry imports this module's job class.
 */
export const MD_ENGINES = [
    {
        key: 'stormm',
        jobtype: 'stormm_md',
        label: 'STORMM (NVT)',
        description: 'Constant volume, Langevin thermostat; the box density comes from an OpenMM NPT preparation. Independent runs are batched on one GPU.',
        // What the engine can run: mirrors md_spec.ENGINE_FEATURES.
        features: {
            dynamics: ['langevin'], backboneRestraint: true, annealing: false, replicaExchange: false,
        },
    },
    {
        key: 'gromacs',
        jobtype: 'gromacs_md',
        label: 'GROMACS (NPT)',
        description: 'Constant pressure (C-rescale, 1 bar), Langevin (sd) or Nosé–Hoover thermostat.',
        features: {
            dynamics: ['langevin', 'nose_hoover'], backboneRestraint: true, annealing: true, replicaExchange: true,
        },
    },
    {
        key: 'openmm',
        jobtype: 'openmm_md',
        label: 'OpenMM (NPT)',
        description: 'Constant pressure (Monte Carlo barostat, 1 bar), LangevinMiddle or Nosé–Hoover integrator. One process per run.',
        features: {
            dynamics: ['langevin', 'nose_hoover'], backboneRestraint: true, annealing: true, replicaExchange: false,
        },
    },
];

/** How the temperature is held (params.dynamics). */
export const MD_DYNAMICS = [
    { key: 'langevin', label: 'Stochastic dynamics (Langevin)' },
    { key: 'nose_hoover', label: 'Deterministic MD (Nosé–Hoover thermostat)' },
];

/** Limits of the options (md_spec.py); the form checks them before submitting. */
export const MD_OPTION_LIMITS = {
    maxBackboneRestraint: 100,
    maxSaltMolar: 1,
    annealing: { minK: 200, maxK: 500, maxPoints: 20 },
    replicaExchange: { minReplicas: 2, maxReplicas: 16, maxTemperatureK: 500 },
};

/** The options a job starts with: none of them on. */
export const MD_OPTION_DEFAULTS = {
    dynamics: 'langevin',
    backboneRestraint: '0',
    neutralize: true,
    saltMolar: '0',
    annealing: false,
    annealingText: '',
    replicaExchange: false,
    replicas: '4',
    maxTemperatureK: '340',
    exchangePs: '1',
};

/**
 * Parse an annealing schedule typed as "time:temperature" pairs, e.g.
 * "0:300, 200:400, 500:300" (ps : K).
 * @param {string} text
 * @returns {{ points?: number[][], error?: string }}
 */
export function parseAnnealing(text) {
    const items = String(text || '').split(/[,;\n]+/).map((x) => x.trim()).filter(Boolean);
    const points = [];
    for (const item of items) {
        const m = item.match(/^([0-9.]+)\s*(?:ps)?\s*[:=]\s*([0-9.]+)\s*(?:K)?$/i);
        if (!m) return { error: `"${item}" is not time:temperature (e.g. 200:400)` };
        points.push([Number(m[1]), Number(m[2])]);
    }
    return { points };
}

/**
 * A geometric temperature ladder (md_spec.remd_temperatures).
 * @param {number} tMin
 * @param {number} tMax
 * @param {number} n
 * @returns {number[]}
 */
export function remdTemperatures(tMin, tMax, n) {
    if (n < 2) return [tMin];
    const ratio = (tMax / tMin) ** (1 / (n - 1));
    return Array.from({ length: n }, (_, i) => Math.round(tMin * ratio ** i * 100) / 100);
}

/** Neighbor spacing (fractional) at which a ~60,000-atom solvated complex still swaps. */
export const REMD_TYPICAL_SPACING = 0.005;

/**
 * A warning when the ladder's neighbors are too far apart to exchange, with
 * the replica count or top temperature that would work (null when fine). The
 * 0.5% spacing is for a typical ~60,000-atom complex in water; smaller systems
 * tolerate more and larger ones less.
 * @param {number} tMin
 * @param {number} tMax
 * @param {number} n - replicas
 * @returns {string|null}
 */
export function remdSpacingWarning(tMin, tMax, n) {
    if (!(tMin > 0) || !(tMax > tMin) || !(n >= 2)) return null;
    const spacing = (tMax / tMin) ** (1 / (n - 1)) - 1;
    if (spacing <= REMD_TYPICAL_SPACING * 1.05) return null;
    const lim = MD_OPTION_LIMITS.replicaExchange;
    const needed = Math.ceil(Math.log(tMax / tMin) / Math.log(1 + REMD_TYPICAL_SPACING)) + 1;
    const head = `Neighbor replicas are ${(spacing * 100).toFixed(1)}% apart in temperature; a complex of this size swaps only when they are about ${REMD_TYPICAL_SPACING * 100}% apart, so few or no exchanges are likely.`;
    if (needed <= lim.maxReplicas) return `${head} Use ${needed} replicas, or lower the top temperature.`;
    const top = Math.floor(tMin * (1 + REMD_TYPICAL_SPACING) ** (lim.maxReplicas - 1));
    return `${head} ${tMin}–${tMax} K would need ${needed} replicas (at most ${lim.maxReplicas}): lower the top temperature to about ${top} K (with ${lim.maxReplicas} replicas).`;
}

/**
 * Why these options cannot be submitted with this engine, or null. Mirrors
 * md_spec.normalize_params, so the form refuses what the job would.
 * @param {object} options - see MD_OPTION_DEFAULTS
 * @param {object} params - the job's params (length_ns, copies, temperature_k)
 * @param {string} engine - an MD_ENGINES key
 * @param {number} [count] - compounds (for the MD budget with replica exchange)
 * @returns {string|null}
 */
export function mdOptionsProblem(options, params, engine, count=1) {
    const spec = MD_ENGINES.find((e) => e.key === engine);
    const f = spec?.features || {};
    const o = { ...MD_OPTION_DEFAULTS, ...(options || {}) };
    const label = spec?.label || engine;
    if (!(f.dynamics || ['langevin']).includes(o.dynamics)) {
        return `${label} cannot run ${MD_DYNAMICS.find((d) => d.key === o.dynamics)?.label || o.dynamics}`;
    }
    const k = Number(o.backboneRestraint || 0);
    if (!Number.isFinite(k) || k < 0 || k > MD_OPTION_LIMITS.maxBackboneRestraint) {
        return `Backbone restraint must be between 0 and ${MD_OPTION_LIMITS.maxBackboneRestraint} kcal/mol/Å²`;
    }
    if (k > 0 && !f.backboneRestraint) return `${label} cannot restrain the backbone`;
    const salt = Number(o.saltMolar || 0);
    if (!Number.isFinite(salt) || salt < 0 || salt > MD_OPTION_LIMITS.maxSaltMolar) {
        return `Salt concentration must be between 0 and ${MD_OPTION_LIMITS.maxSaltMolar} M`;
    }
    if (o.neutralize === false && salt > 0) {
        return 'Salt is added on top of neutralizing: set the salt to 0, or neutralize the system';
    }
    const t0 = Number(params?.temperature_k);
    const lengthPs = Number(params?.length_ns) * 1000;
    if (o.annealing) {
        if (!f.annealing) return `${label} cannot anneal (change the temperature during a run)`;
        const { points, error } = parseAnnealing(o.annealingText);
        if (error) return `Annealing: ${error}`;
        const lim = MD_OPTION_LIMITS.annealing;
        if (points.length < 2 || points.length > lim.maxPoints) {
            return `Annealing needs 2 to ${lim.maxPoints} time:temperature points`;
        }
        if (points[0][0] !== 0) return 'Annealing must start at time 0';
        if (Math.abs(points[0][1] - t0) > 0.5) {
            return `Annealing must start at the run's temperature (${t0} K)`;
        }
        if (points.some((pt, i) => i > 0 && pt[0] <= points[i - 1][0])) return 'Annealing times must increase';
        if (points[points.length - 1][0] > lengthPs + 1e-6) {
            return `Annealing runs past the end of the run (${lengthPs} ps)`;
        }
        if (points.some(([, t]) => t < lim.minK || t > lim.maxK)) {
            return `Annealing temperatures must be between ${lim.minK} and ${lim.maxK} K`;
        }
    }
    if (o.replicaExchange) {
        if (!f.replicaExchange) return `${label} cannot run replica exchange`;
        if (o.annealing) return 'Replica exchange and annealing cannot be combined';
        const lim = MD_OPTION_LIMITS.replicaExchange;
        const n = Number(o.replicas);
        if (!Number.isInteger(n) || n < lim.minReplicas || n > lim.maxReplicas) {
            return `Replicas must be a whole number from ${lim.minReplicas} to ${lim.maxReplicas}`;
        }
        const tMax = Number(o.maxTemperatureK);
        if (!Number.isFinite(tMax) || tMax < t0 + 1 || tMax > lim.maxTemperatureK) {
            return `The top temperature must be above ${t0} K and at most ${lim.maxTemperatureK} K`;
        }
        const ex = Number(o.exchangePs);
        if (!Number.isInteger(ex) || ex < 1 || ex > 100) {
            return 'The exchange interval must be a whole number of ps (1-100)';
        }
        if (Number(params?.copies) !== 1) return 'With replica exchange, independent runs must be 1 (the replicas take their place)';
        const totalNs = Number(params.length_ns) * n * count;
        if (totalNs > MD_LIMITS.maxTotalNs + 1e-9) {
            return `${count} compound(s) × ${n} replicas × ${params.length_ns} ns = ${+totalNs.toFixed(3)} ns of MD, more than the ${MD_LIMITS.maxTotalNs} ns allowed per job`;
        }
    }
    return null;
}

/**
 * The request params the options become (md_spec's names).
 * @param {object} options - see MD_OPTION_DEFAULTS
 * @returns {object}
 */
export function mdOptionParams(options) {
    const o = { ...MD_OPTION_DEFAULTS, ...(options || {}) };
    const out = { dynamics: o.dynamics };
    const k = Number(o.backboneRestraint || 0);
    if (k > 0) out.backbone_restraint = k;
    const salt = Number(o.saltMolar || 0);
    if (salt > 0) out.salt_molar = salt;
    if (o.neutralize === false) out.neutralize = false;
    if (o.annealing) out.annealing = parseAnnealing(o.annealingText).points;
    if (o.replicaExchange) {
        out.replica_exchange = {
            replicas: Number(o.replicas),
            max_temperature_k: Number(o.maxTemperatureK),
            exchange_ps: Number(o.exchangePs),
        };
    }
    return out;
}

/**
 * Where an engine's job can compute (compute_targets.js decides): the
 * request's target picks the queue.
 * @param {string} engine - an MD_ENGINES key
 * @returns {{ key: string, label: string, disabled: boolean }[]}
 */
export function mdTargets(engine) {
    const jobtype = MD_ENGINES.find((e) => e.key === engine)?.jobtype;
    return targetOptions(jobtype || 'stormm_md');
}

/** The STORMM engine's targets (kept for callers that predate per-engine targets). */
export const MD_TARGETS = mdTargets('stormm');

/** How ligand partial charges are obtained. */
export const MD_LIGAND_CHARGES = [
    { key: 'am1bcc', label: 'Compute AM1-BCC charges in the job' },
    { key: 'submitted', label: 'Use the charges computed in BMaps' },
];

/** The backend's limits (md_spec.py); the form checks them before submitting. */
export const MD_LIMITS = {
    maxCompounds: 10,
    maxFramesPerCopy: 2000,
    maxTotalNs: 100,
    params: {
        length_ns: { min: 0.01, max: 100, label: 'Length (ns)' },
        copies: {
            min: 1, max: 8, label: 'Independent runs', integer: true,
        },
        temperature_k: { min: 250, max: 400, label: 'Temperature (K)' },
        frame_interval_ps: { min: 1, max: 1000, label: 'Frame interval (ps)' },
        skip_ps: { min: 0, max: null, label: 'Skipped at the start (ps)' },
    },
};

export const MD_DEFAULTS = {
    length_ns: 1, copies: 3, temperature_k: 300, frame_interval_ps: 10, skip_ps: 10,
};

/** GPU minutes per ns per copy for a ~60k-atom complex (measured on ConiferGPU2). */
const GPU_MINUTES_PER_NS = { stormm: 4, gromacs: 5, openmm: 3 };
/** The per-compound preparation (charges, build, OpenMM NPT), minutes. */
const PREP_MINUTES = 3;

/**
 * Why these parameters cannot be submitted, or null. Mirrors the backend's
 * validation (md_spec.normalize_params), so the form refuses what the job
 * would refuse.
 * @param {object} params - length_ns, copies, temperature_k, frame_interval_ps, skip_ps
 * @param {number} count - compounds
 * @returns {string|null}
 */
export function mdParamsProblem(params, count) {
    if (!count || count < 1) return 'Select at least one compound to simulate';
    if (count > MD_LIMITS.maxCompounds) {
        return `${count} compounds is more than the ${MD_LIMITS.maxCompounds} one MD job may run`;
    }
    for (const [name, spec] of Object.entries(MD_LIMITS.params)) {
        const v = Number(params?.[name]);
        if (params?.[name] === '' || params?.[name] === undefined || !Number.isFinite(v)) {
            return `${spec.label} must be a number`;
        }
        if (spec.integer && !Number.isInteger(v)) return `${spec.label} must be a whole number`;
        if (v < spec.min || (spec.max !== null && v > spec.max)) {
            return spec.max !== null
                ? `${spec.label} must be between ${spec.min} and ${spec.max}`
                : `${spec.label} must be at least ${spec.min}`;
        }
    }
    const lengthPs = Number(params.length_ns) * 1000;
    const interval = Number(params.frame_interval_ps);
    if (Number(params.skip_ps) >= lengthPs) {
        return `The skipped start (${params.skip_ps} ps) must be shorter than the run (${lengthPs} ps)`;
    }
    if (lengthPs / interval > MD_LIMITS.maxFramesPerCopy) {
        return `${Math.round(lengthPs / interval)} frames per copy is more than ${MD_LIMITS.maxFramesPerCopy}: raise the frame interval or shorten the run`;
    }
    if ((lengthPs - Number(params.skip_ps)) / interval < 2) {
        return 'Fewer than 2 frames would be left to analyze: lower the frame interval or the skipped start';
    }
    const totalNs = Number(params.length_ns) * Number(params.copies) * count;
    if (totalNs > MD_LIMITS.maxTotalNs + 1e-9) {
        return `${count} compound(s) × ${params.copies} copies × ${params.length_ns} ns = ${+totalNs.toFixed(3)} ns of MD, more than the ${MD_LIMITS.maxTotalNs} ns allowed per job`;
    }
    return null;
}

/**
 * Rough GPU time for a job, in minutes (for the form's estimate only).
 * @param {object} params
 * @param {number} count
 * @param {'stormm'|'gromacs'|'openmm'} engine
 * @param {object} [options] - with replicaExchange, each replica is a run
 * @returns {number}
 */
export function estimateGpuMinutes(params, count, engine='stormm', options) {
    const perNs = GPU_MINUTES_PER_NS[engine] || GPU_MINUTES_PER_NS.stormm;
    const runs = options?.replicaExchange ? Number(options.replicas) : Number(params.copies);
    const md = Number(params.length_ns) * runs * count * perNs;
    return Math.round(md + PREP_MINUTES * count);
}

/**
 * The request the backend's MD workflow consumes. Pure, so the payload shape
 * is unit-testable without a workspace.
 * @param {object} p
 * @param {string} p.pdb - receptor PDB text
 * @param {string} p.ligandSdf - ligands, titles = compound resSpec
 * @param {object} p.params - see MD_DEFAULTS
 * @param {number} p.ligandCount
 * @param {string} [p.jobName]
 * @param {'termini'|'full'} [p.receptorPrep]
 * @param {'am1bcc'|'submitted'} [p.ligandCharges]
 * @param {string} [p.target]
 * @param {number} [p.maxRuntimeMinutes] - may only lower the worker's cap
 * @param {object} [p.options] - see MD_OPTION_DEFAULTS
 * @returns {object}
 */
export function buildMdRequest({
    pdb, ligandSdf, params, ligandCount, jobName, receptorPrep, ligandCharges,
    target='conifergpu2', maxRuntimeMinutes, options,
}) {
    const p = {
        length_ns: Number(params.length_ns),
        copies: Number(params.copies),
        temperature_k: Number(params.temperature_k),
        frame_interval_ps: Number(params.frame_interval_ps),
        skip_ps: Number(params.skip_ps),
        target,
        ...(options ? mdOptionParams(options) : {}),
    };
    if (receptorPrep) p.receptor_prep = receptorPrep;
    if (ligandCharges) p.ligand_charges = ligandCharges;
    if (maxRuntimeMinutes > 0) p.max_runtime = Math.round(maxRuntimeMinutes);
    return {
        receptor_pdb: pdb,
        ligand_sdf: ligandSdf,
        params: p,
        ligand_count: ligandCount,
        jobName: jobName || `MD: ${ligandCount} compound${ligandCount === 1 ? '' : 's'} (${p.length_ns} ns × ${p.copies})`,
    };
}

/**
 * The BMaps residue key "CHAIN:RESNUM[ICODE]" of an atom, the form the MD
 * packet names residues in (FEP's residueSpecOf leaves out the insertion code).
 * @param {{ chain?: string, resi: number|string, icode?: string }} atom
 * @returns {string}
 */
export function residueKeyOfAtom(atom) {
    return `${atom.chain || ''}:${atom.resi}${atom.icode || ''}`;
}

/** Element symbols of a V2000 mol block's atoms, in order. */
export function molBlockElements(molBlock) {
    const lines = String(molBlock || '').split(/\r?\n/);
    const n = parseInt((lines[3] || '').slice(0, 3), 10);
    if (!Number.isFinite(n)) return null;
    return lines.slice(4, 4 + n).map((ln) => ln.slice(31, 34).trim());
}

/**
 * The compound's per-atom partial charges in its mol block's atom order, for
 * `ligand_charges: 'submitted'`. BMaps leaves `atom.charge` undefined both for
 * a zero charge and for an atom never charged, so a compound none of whose
 * atoms carries a non-zero charge has no charges yet (ask the server with
 * UserActions.GetForcefieldParameters). Refuses when the atoms do not line up
 * with the mol block (count or element order), since the charges are matched
 * to the mol block's atoms by position.
 * @param {import('BMapsModel').Compound} compound
 * @returns {{ charges?: number[], error?: string }}
 */
export function ligandChargesFor(compound) {
    const atoms = compound.getAtoms();
    if (!atoms.some((a) => a.charge)) {
        return { error: `${compound.resSpec} has no partial charges in BMaps yet` };
    }
    const elements = molBlockElements(compound.getMol2000());
    const mine = atoms.map((a) => String(a.elem || '').trim());
    if (!elements || elements.length !== atoms.length
        || elements.some((e, i) => e.toUpperCase() !== mine[i].toUpperCase())) {
        return { error: `${compound.resSpec}: its atoms do not line up with its mol block, so its charges cannot be sent` };
    }
    return { charges: atoms.map((a) => (Number.isFinite(a.charge) ? a.charge : 0)) };
}

/**
 * An SDF record with the charges added as an `atom.dprop.PartialCharge` data
 * item (the property RDKit and bfd-server read; the backend accepts it).
 * @param {string} record - one record, ending with $$$$
 * @param {number[]} charges
 * @returns {string}
 */
export function sdfWithCharges(record, charges) {
    const body = String(record).replace(/\$\$\$\$\s*$/, '').replace(/\s*$/, '');
    const values = charges.map((q) => q.toFixed(4)).join(' ');
    // A data item follows M  END directly, or the blank line that ends the
    // previous item (dropping that line merges the two items' values).
    const sep = /M {2}END$/.test(body) ? '\n' : '\n\n';
    return `${body}${sep}>  <atom.dprop.PartialCharge>\n${values}\n\n$$$$\n`;
}

/**
 * Submit an MD job for a set of compounds on one structure.
 *
 * Like an FEP job, this stages a persistent jobman job and returns at once:
 * the run takes minutes to hours on a GPU. It appears in the Molecular
 * Dynamics panel's Jobs tab; its results are viewed in the Results tab.
 *
 * @param {import('BMapsModel').Compound|import('BMapsModel').Compound[]} compoundsIn
 * @param {object} opts
 * @param {'stormm'|'gromacs'|'openmm'} [opts.engine]
 * @param {object} [opts.params] - see MD_DEFAULTS
 * @param {string[]} [opts.chains] - receptor chains (all when omitted)
 * @param {object[]} [opts.ions] - ion atom groups kept in the receptor
 * @param {'termini'|'full'} [opts.receptorPrep]
 * @param {'am1bcc'|'submitted'} [opts.ligandCharges]
 * @param {string} [opts.target]
 * @param {number} [opts.maxRuntimeMinutes]
 * @param {object} [opts.options] - dynamics, backbone restraint, annealing,
 *   replica exchange (see MD_OPTION_DEFAULTS)
 * @param {string} [opts.jobName]
 * @returns {Promise<{ results?: object, error?: string, jobtype?: string }>}
 */
export async function startMdSimulation(compoundsIn, {
    engine='stormm', params=MD_DEFAULTS, options, chains, ions: ionsIn, receptorPrep,
    ligandCharges='am1bcc', target='conifergpu2', maxRuntimeMinutes, jobName,
} = {}) {
    const compounds = ensureArray(compoundsIn);
    const spec = MD_ENGINES.find((e) => e.key === engine);
    if (!spec) return { error: `Unknown MD engine: ${engine}` };
    const problem = mdParamsProblem(params, compounds.length)
        || mdOptionsProblem(options, params, engine, compounds.length);
    if (problem) return { error: problem };
    const targetError = targetProblem(spec.jobtype, target);
    if (targetError) return { error: targetError };
    if (chains && chains.length === 0) return { error: 'Select at least one receptor chain.' };
    const ions = ensureArray(ionsIn || []);
    const { dataParentsList } = App.partitionByDataParents([...compounds, ...ions]);
    if (dataParentsList.length > 1) {
        return { error: 'An MD job needs all compounds (and ions) from the same protein structure.' };
    }

    const { pdb, error, pdbObj } = await UserActions.GetProteinPdb(
        compounds[0], { method: 'local', chains, ions },
    );
    if (error) return { error };
    if (!pdbObj || pdbObj.residues.length === 0 || !pdb) {
        return { error: 'No protein available for the MD receptor.' };
    }

    // SDF titles MUST be the compounds' resSpec: results come back under them.
    const sdfOptions = {
        useSpecForName: true,
        propInclusion: 'custom',
        customPropFn: (c) => ({ bmapsResSpec: c.resSpec }),
    };
    let ligandSdf;
    if (ligandCharges === 'submitted') {
        // Make sure every atom carries its charge: BMaps leaves a zero charge
        // undefined, and skips large ligands on load, until the server is
        // asked for the forcefield parameters (cached server-side).
        const { errors: ffErrors } = await UserActions.GetForcefieldParameters(compounds);
        if (ffErrors?.length) {
            return { error: `Could not get the BMaps charges: ${ffErrors.join('; ')}` };
        }
        const parts = [];
        for (const c of compounds) {
            const { charges, error: chargeError } = ligandChargesFor(c);
            if (chargeError) return { error: chargeError };
            parts.push(sdfWithCharges(Compound.makeSDF([c], sdfOptions), charges));
        }
        ligandSdf = parts.join('');
    } else {
        ligandSdf = Compound.makeSDF(compounds, sdfOptions);
    }

    const requestData = buildMdRequest({
        pdb,
        ligandSdf,
        params,
        ligandCount: compounds.length,
        jobName,
        receptorPrep,
        ligandCharges,
        target,
        maxRuntimeMinutes,
        options,
    });
    const { mapCase } = App.getDataParents(compounds[0]) || {};
    const stash = {
        engine,
        ligandSpecs: compounds.map((c) => c.resSpec),
        ionSpecs: ions.map((g) => g.resSpec),
        chains: chains || null,
        structure: { name: mapCase?.displayName || null },
    };
    const response = await createJob(spec.jobtype, requestData, stash);
    return { ...response, jobtype: spec.jobtype };
}

/**
 * The workspace compounds an MD job's results belong to (by resSpec, on the
 * structure it was submitted from), from the stash returned with the results
 * or the job list's request metadata.
 * @param {object} job
 * @param {import('BMapsModel').Compound[]} [loaded]
 * @returns {import('BMapsModel').Compound[]}
 */
export function mdCandidateCompounds(job, loaded) {
    const scope = job?.stash || MdSimulationJob.scopeFromRequestMeta(job);
    return MdSimulationJob.candidateCompounds(scope, loaded);
}

/**
 * Write a finished MD job's per-compound properties (pose verdict, ligand
 * RMSD, top H-bond) onto the compounds it was submitted for.
 * @param {string} jobid
 * @returns {Promise<object>}
 */
export async function applyMdResults(jobid) {
    return loadResult(jobid, { apply: true });
}

/** The time-series plots the Results tab offers (keys of a copy's `series`). */
export const MD_PLOTS = [
    { key: 'ligand_rmsd_a', label: 'Ligand RMSD from the submitted pose (Å)' },
    { key: 'backbone_rmsd_a', label: 'Backbone RMSD (Å)' },
    { key: 'hbond_count', label: 'Protein–ligand H-bonds' },
    { key: 'temperature_k', label: 'Temperature (K)' },
    { key: 'potential_kcal', label: 'Potential energy (kcal/mol)' },
    { key: 'density_kg_m3', label: 'Density (kg/m³)' },
];

/**
 * The plots a compound's copies have data for.
 * @param {object} compound - a `compounds` entry of the md/1 packet
 * @returns {{ key: string, label: string }[]}
 */
export function availablePlots(compound) {
    const copies = compound?.copies || [];
    const hasData = (key) => copies.some((c) => (c.series?.[key] || []).some((v) => v !== null));
    return MD_PLOTS.filter((p) => hasData(p.key));
}

/**
 * One chart line per copy: `[time_ps, value]` points of the series `key`.
 * @param {object} compound
 * @param {string} key
 * @returns {{ name: string, points: Array<[number, number|null]> }[]}
 */
export function seriesForPlot(compound, key) {
    return (compound?.copies || []).map((c) => {
        const t = c.series?.time_ps || [];
        const v = c.series?.[key] || [];
        return { name: `copy ${c.copy}`, points: t.map((x, i) => [x, v[i] ?? null]) };
    });
}

/**
 * Per-residue CA RMSF averaged over the copies, in sequence order.
 * @param {object} compound
 * @returns {{ residue: string, resname: string, rmsf_a: number }[]}
 */
export function meanRmsf(compound) {
    const sums = new Map();
    for (const c of compound?.copies || []) {
        for (const r of c.rmsf_ca || []) {
            const e = sums.get(r.residue) || {
                residue: r.residue, resname: r.resname, total: 0, n: 0,
            };
            e.total += r.rmsf_a;
            e.n += 1;
            sums.set(r.residue, e);
        }
    }
    return [...sums.values()].map((e) => ({
        residue: e.residue, resname: e.resname, rmsf_a: Number((e.total / e.n).toFixed(3)),
    }));
}

/** How a pose verdict reads, with a color. */
export const POSE_STYLES = {
    stable: { label: 'stable', color: '#2e7d32' },
    shifting: { label: 'shifting', color: '#b8650c' },
    unstable: { label: 'unstable', color: '#c62828' },
};
